import React, { useState } from 'react';

const TESTIMONIALS = [
  {
    name: 'Sophie M.',
    location: 'New York, NY',
    rating: 5,
    verified: true,
    quote: `I finally understood why my hair was always frizzy. The AI told me I had low-porosity hair and recommended products that made a difference in weeks.`,
    imageUrl: encodeURI('/images/testimonials/Sophie M_testimonial.png'),
    alt: 'Photo of Sophie M., New York, NY'
  },
  {
    name: 'Daniel K.',
    location: 'Austin, TX',
    rating: 5,
    verified: true,
    quote: `I've saved so much money by not buying the wrong products anymore. The AI's recommendations for my oily scalp were spot-on.`,
    imageUrl: encodeURI('/images/testimonials/Daniel K_testimonial.png'),
    alt: 'Photo of Daniel K., Austin, TX'
  },
  {
    name: 'Amira L.',
    location: 'London, UK',
    rating: 5,
    verified: true,
    quote: `My breakage has reduced significantly. The analysis explained exactly what my hair needed, and the routine is easy to follow.`,
    imageUrl: encodeURI('/images/testimonials/Amira L_testimonial.png'),
    alt: 'Photo of Amira L., London, UK'
  },
  {
    name: 'Carla R.',
    location: 'Miami, FL',
    rating: 5,
    verified: true,
    quote: `After years of coloring, my hair felt dead. The personalized routine brought back the shine and softness I thought was gone forever.`,
    imageUrl: encodeURI('/images/testimonials/Carla R_testimonial.png'),
    alt: 'Photo of Carla R., Miami, FL'
  },
  {
    name: 'Hannah P.',
    location: 'Toronto, Canada',
    rating: 5,
    verified: true,
    quote: `This is a must-have for anyone with curls. Understanding my porosity level was a game-changer for moisture and definition.`,
    imageUrl: encodeURI('/images/testimonials/Hannah P_testimonial.png'),
    alt: 'Photo of Hannah P., Toronto, Canada'
  }
];

function Stars({ count = 5 }) {
  return (
    <div className="flex items-center text-yellow-400" aria-label={`${count} star rating`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <span key={i} className={i < count ? '' : 'text-gray-300'}>★</span>
      ))}
    </div>
  );
}

function InitialsBlock({ name }) {
  const initials = name.split(' ').map(n => n[0]).slice(0,2).join('');
  return (
    <div className="relative w-full h-72 md:h-96 bg-white/10 flex items-center justify-center">
      <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center text-white text-2xl font-semibold">{initials}</div>
    </div>
  );
}

function TestimonialImage({ imageUrl, alt, name, beforeUrl, afterUrl }) {
  // If both before and after are provided in the future, render side-by-side
  if (beforeUrl && afterUrl) {
    return (
      <div className="grid grid-cols-2 gap-1 w-full h-72 md:h-96 bg-black">
        <img src={beforeUrl} alt={(alt || name) + ' - before'} className="w-full h-full object-cover md:object-contain object-center" loading="lazy" />
        <img src={afterUrl} alt={(alt || name) + ' - after'} className="w-full h-full object-cover md:object-contain object-center" loading="lazy" />
      </div>
    );
  }
  // Single image fallback
  if (imageUrl) {
    return (
      <div className="w-full h-72 md:h-96 bg-black">
        <img src={imageUrl} alt={alt || name} className="w-full h-full object-cover md:object-contain object-center" loading="lazy" />
      </div>
    );
  }
  // No image yet
  return <InitialsBlock name={name} />;
}

function VerifiedBadge() {
  return <span className="ml-2 text-xs text-green-300">✔ Verified Buyer</span>;
}

export default function Testimonials() {
  const [expanded, setExpanded] = useState(false);
  const [current, setCurrent] = useState(0);
  const len = TESTIMONIALS.length;
  const prev = () => setCurrent(i => (i - 1 + len) % len);
  const next = () => setCurrent(i => (i + 1) % len);
  const [startX, setStartX] = useState(null);
  const handleTouchStart = (e) => setStartX(e.touches?.[0]?.clientX ?? null);
  const handleTouchEnd = (e) => {
    if (startX === null) return;
    const x = e.changedTouches?.[0]?.clientX ?? 0;
    const dx = x - startX;
    if (Math.abs(dx) > 40) {
      dx < 0 ? next() : prev();
    }
    setStartX(null);
  };

  return (
    <section className="mt-12 p-4 sm:p-6 md:p-8 bg-black bg-opacity-30 backdrop-filter backdrop-blur-sm rounded-none sm:rounded-2xl border border-white/20 -mx-8 sm:mx-0">
      <div className="text-center">
        <h3 className="text-2xl md:text-3xl font-semibold text-white">Real People. Real Hair Transformations.</h3>
        <button
          type="button"
          aria-expanded={expanded}
          onClick={() => setExpanded(v => !v)}
          className="mt-4 inline-flex items-center px-6 py-3 rounded-full bg-white/15 border border-white/25 text-white font-medium hover:bg-white/25 transition"
        >
          {expanded ? 'Hide Testimonials' : 'Testimonials'}
        </button>
      </div>
      {/* Carousel (single card focus) */}
      {expanded && (
        <div className="mt-10">
          <div
            className="relative max-w-3xl mx-auto"
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            <article className="bg-white/10 rounded-2xl overflow-hidden border border-white/20">
              <TestimonialImage imageUrl={TESTIMONIALS[current].imageUrl} alt={TESTIMONIALS[current].alt} name={TESTIMONIALS[current].name} beforeUrl={TESTIMONIALS[current].beforeUrl} afterUrl={TESTIMONIALS[current].afterUrl} />
              <div className="p-5 md:p-6">
                <div className="text-white font-medium flex items-center">
                  {TESTIMONIALS[current].name}
                  {TESTIMONIALS[current].verified && <VerifiedBadge />}
                </div>
                <div className="text-gray-300 text-sm mt-1">{TESTIMONIALS[current].location}</div>
                <div className="mt-2"><Stars count={TESTIMONIALS[current].rating} /></div>
                <p className="text-gray-100 mt-3 italic leading-relaxed">“{TESTIMONIALS[current].quote}”</p>
              </div>
            </article>

            {/* Nav buttons */}
            <button
              type="button"
              onClick={prev}
              aria-label="Previous testimonial"
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 md:-translate-x-full w-10 h-10 rounded-full bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm"
            >
              ‹
            </button>
            <button
              type="button"
              onClick={next}
              aria-label="Next testimonial"
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 md:translate-x-full w-10 h-10 rounded-full bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm"
            >
              ›
            </button>

            {/* Dots */}
            <div className="flex items-center justify-center gap-2 mt-4">
              {TESTIMONIALS.map((_, i) => (
                <button
                  key={i}
                  aria-label={`Go to testimonial ${i + 1}`}
                  onClick={() => setCurrent(i)}
                  className={`w-2.5 h-2.5 rounded-full ${i === current ? 'bg-white' : 'bg-white/40'} hover:bg-white/80`}
                />
              ))}
            </div>
          </div>
        </div>
      )}


      {/* Small disclaimer */}
      <p className="text-xs text-gray-300 mt-6 text-center">
        Reviews reflect personal experiences. Results vary based on hair condition and routine adherence.
      </p>
    </section>
  );
}

